import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import sessionId from './session-service';

// --- Public API ---

export interface DatadogEnvironment {
  dataDogApplicationId: string;
  dataDogClientToken: string;
  environment: string;
  releaseSha: string;
}

/**
 * Initialize Datadog RUM, Logs, and Session Replay, then tag early context
 * (URL params, client session ID) so even pre-auth events are attributable.
 */
export function initDatadog(env: DatadogEnvironment, newSession: boolean): void {
  datadogRum.init({
    applicationId: env.dataDogApplicationId,
    clientToken: env.dataDogClientToken,
    site: 'datadoghq.com',
    service: 'agencyapi-client',
    env: env.environment,
    version: env.releaseSha,
    enableExperimentalFeatures: ['feature_flags'],
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    compressIntakeRequests: true,
    allowedTracingUrls: [
      /https?:\/\/localhost(:\d+)?/,
      /https?:\/\/(.+\.)?joinroot.com(:\d+)?/,
      /https?:\/\/(.+\.)?root.com(:\d+)?/,
    ],
    trackSessionAcrossSubdomains: false,
  });

  datadogLogs.init({
    clientToken: env.dataDogClientToken,
    site: 'datadoghq.com',
    service: 'agencyapi-client',
    env: env.environment,
    version: env.releaseSha,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: 'all',
    forwardReports: 'all',
    sessionSampleRate: 100,
    trackSessionAcrossSubdomains: false,
  });

  if (newSession) {
    // If this is a new session (e.g. user just authenticated), stop the previous session's recording. A new session will be started automatically, on the next user interaction.
    datadogRum.stopSession();
  }

  // Start session replay recording - must be called after init()
  datadogRum.startSessionReplayRecording();

  // Set early context from URL params and session ID so pre-auth events are attributable
  const params = new URLSearchParams(window.location.search);
  datadogRum.setGlobalContextProperty('clientSessionId', sessionId());
  datadogRum.setGlobalContextProperty('requestedExperience', params.get('experience'));
  datadogRum.setGlobalContextProperty('requestedUtmSource', params.get('utm_source'));
}

/**
 * Update RUM user context and feature flag evaluations after step data loads.
 * Called from trackScene with data from the server's scene response.
 */
export function updateSessionContext({
  quote_id,
  agent_id,
  market,
  experience,
  experiments,
}: {
  quote_id: string | null;
  agent_id: string | null;
  market: string | null;
  experience: string;
  experiments: Record<string, string>;
}): void {
  datadogRum.setUser({
    ...datadogRum.getUser(),
    id: sessionId(),
    quoteId: quote_id,
    agentId: agent_id,
    market,
    experience,
  });

  datadogRum.setGlobalContextProperty('experience', experience);

  Object.entries(experiments).forEach(([key, value]) => {
    datadogRum.addFeatureFlagEvaluation(key, value);
  });
}
